import React from 'react';

import { IFullwidthHero20Fields } from '@/types/contentful';

import { getImageDataFromOptions } from '../../../utils/contentful';
import { usePageDataContext } from '../../../context/PageData';

import PrivacyPolicyDisclaimer from '../../PrivacyPolicyDisclaimer';
import CtaWidget from '../../CtaWidget';
import { VideoPlayer } from '../../VideoPlayer';

import { MobileImage } from '../styles';
import { AdditionalText } from '../AdditionalText';
import { Eyebrow } from '../../Eyebrow';
import { SocialProofBar } from '../../SocialProofBar';
import { TextWithOptions } from '~/components/Text/TextWithOptions';

interface Props extends IFullwidthHero20Fields {
  isReturning?: boolean;
}

export default function MobileView({
  mobileHeadingText,
  mobileHeadingTextOptions,
  mobileSubheadingText,
  mobileSubheadingTextOptions,
  mobileImage,
  mobileImageOptions,
  additionalText,
  mobilePrivacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  privacyPolicyDisclaimer,
  videoModule,
  mobileCta,
  eyebrow,
  socialProofBar,
  isReturning,
  returningUserHeadingText
}: Props) {
  const {
    cookies: { utm_source = '' } = {},
    query: { gl_content, g_adgroupid } = {},
    advocateUserData
  } = usePageDataContext();

  return (
    <section
      className="block-container mb-20 flex-col items-center desktop:hidden"
      data-testid="fullwidth-hero-mobile"
    >
      <div className="flex flex-col items-center justify-center self-stretch tablet:gap-6 tablet:px-10 tablet:pt-8">
        <div className="flex flex-col items-center justify-center gap-3 self-stretch">
          <div className="w-full self-stretch">
            {videoModule?.fields?.vimeoId ? (
              <VideoPlayer {...videoModule?.fields} rounded={false} />
            ) : (
              <MobileImage
                {...getImageDataFromOptions(
                  gl_content,
                  utm_source,
                  g_adgroupid,
                  mobileImage,
                  mobileImageOptions
                )}
                heroImage={true}
                loading="eager"
                data-testid="fullwidth-hero-mobile-background"
              />
            )}
          </div>
        </div>
        {eyebrow && (
          <Eyebrow
            eyebrowFields={eyebrow.fields}
            advocateData={advocateUserData}
          />
        )}
        <div className="mb-4 flex flex-col items-center justify-center gap-6 self-stretch px-5 pt-8 tablet:mb-0 tablet:justify-normal tablet:gap-4 tablet:p-0">
          <TextWithOptions
            as="h2"
            v2
            className="text-center text-[40px] leading-[115%] tablet:text-[52px]"
            keyword={gl_content}
            channel={utm_source}
            adGroupId={g_adgroupid}
            fallback={
              (isReturning
                ? returningUserHeadingText ?? mobileHeadingText
                : mobileHeadingText) || ''
            }
            options={mobileHeadingTextOptions}
            data-testid={'fullwidth-hero-headline-mobile'}
            module="Fullwidth Hero mobile heading"
          />
          {mobileSubheadingText && (
            <TextWithOptions
              as="p"
              v2
              className="text-center text-lg leading-[115%]"
              data-testid="fullwidth-hero-subheading-mobile"
              keyword={gl_content}
              channel={utm_source}
              adGroupId={g_adgroupid}
              fallback={mobileSubheadingText ?? ''}
              options={mobileSubheadingTextOptions}
              module="Fullwidth Hero mobile subheading"
            />
          )}
        </div>
        <div className="flex max-w-[350px] flex-col items-start gap-3 p-0">
          {mobileCta && (
            <CtaWidget
              {...mobileCta?.fields}
              id="fullwidth-hero-mobile-cta"
              sysId={mobileCta?.sys?.contentType?.sys?.id}
              module="Hero"
              isMobile={true}
              width={'100%'}
              responsive
            />
          )}
          {additionalText && <AdditionalText additionalText={additionalText} />}
          <div className="box-border">
            <PrivacyPolicyDisclaimer
              privacyPolicyDisclaimer={
                mobilePrivacyPolicyDisclaimer ?? privacyPolicyDisclaimer
              }
              displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
            />
          </div>
        </div>
      </div>
      {socialProofBar && <SocialProofBar {...socialProofBar.fields} />}
    </section>
  );
}
